<template>
  <v-data-table :headers="headers" :items="formatItems" calculate-widths no-data-text="No rates created">
      <template v-slot:item.los="{ item }">
          <v-chip small>
          {{ item.los }}
          </v-chip>
      </template>
      <template v-slot:item.dates="{ item }">
        <v-chip v-for="date in [...item.dates].splice(0, 1)" :key="date" class="mb-1" small>{{ date }}</v-chip>
        <v-chip small v-if="item.dates.length > 1">
          <primary-tool-tip icon="mdi-dots-horizontal">
            <span v-for="date in item.dates" :key="date"> {{ date }} <br /> </span>
          </primary-tool-tip>
        </v-chip>
      </template>
      <template v-slot:item.ppn="{ item }">
          <v-chip class="mb-2 mr-1" small v-for="ppn in item.ppn" :key="ppn">
          {{ ppn }}
          </v-chip>
      </template>
      <template v-slot:item.rooms="{ item }">
        <div class="d-inline-flex flex-wrap" v-if="item.units.length >= 4" active-class="primary--text" column>
          <v-chip class="mb-2 mr-1" small v-for="unit in [...item.units].splice(0, 3)" :key="unit.id">
              {{ unit.name }}
          </v-chip>

          <div class="d-inline-flex flex-wrap">
              <v-chip class="mb-2 mr-1" small>
              <primary-tool-tip icon="mdi-dots-horizontal">
                  <span v-for="unit in item.units" :key="unit.id"> {{ unit.name }} <br /> </span>
              </primary-tool-tip>
              </v-chip>
          </div>
          </div>
          <div class="d-inline-flex flex-wrap" v-else-if="item.units.length > 0 && item.units.length < 4" active-class="primary--text" column>
          <v-chip class="mb-2 mr-1" small v-for="unit in [...item.units].splice(0, 3)" :key="unit.id">
              {{ unit.name }}
          </v-chip>
        </div>
        <div v-else>
          <v-chip small>*No rooms assigned*</v-chip>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
          <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
          </template>
          <v-list class="options-list">
            <v-list-item>
              <v-list-item-title>
                <router-link :to="{ name: 'place-rates-edit', params: { rateId: item.rate.id, isDuplicate: false } }" title="Edit rate"> <v-icon left small>mdi-pencil</v-icon>Edit </router-link>
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>
                <router-link :to="{ name: 'place-rates-duplicate', params: { rateId: item.rate.id, isDuplicate: true } }" title="Duplicate rate"> <v-icon left small>mdi-content-copy</v-icon>Duplicate </router-link>
              </v-list-item-title>
            </v-list-item>
            <v-list-item v-if="item.units.length === 0">
              <confirmation title="Delete Rate Confirmation" :submitHandler="deleteSubmitHandler" :entity="item.rate">
                  <template v-slot:activator="props">
                  <v-list-item-title @click.stop="props.showDialog" class="red--text"> <v-icon left small class="red--text">mdi-delete</v-icon>{{ $t('global-terms.delete') }} </v-list-item-title>
                  </template>
                  <template v-slot:body>
                  <p>*Are you sure you want to delete this rate?*</p>
                  </template>
              </confirmation>
            </v-list-item>
            <v-list-item v-else>
              <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                  <v-list-item-title v-bind="attrs" v-on="on" class="grey--text "><v-icon left small class="grey--text ">mdi-delete</v-icon>{{ $t('global-terms.delete') }} </v-list-item-title>
                  </template>
                  <span>*A rate with rooms assigned to it cannot be deleted*</span>
              </v-tooltip>
            </v-list-item>
          </v-list>
          </v-menu>
      </template>
  </v-data-table>
</template>
<script>
import { mapActions, mapState } from 'pinia'
import { useRateStore, useUnitStore } from '@/stores'
import { formatRates } from '@/helpers/bookingRules'
import Confirmation from '@/components/dialogs/Confirmation'
import { sortObjects } from '@/helpers/stateUtils'
import PrimaryToolTip from '@/components/tooltips/PrimaryToolTip.vue'
import { clone } from 'ramda'
import moment from 'moment'

export default {
  data () {
    return {
      headers: [
        { text: 'Rate name', value: 'rate.description', sortable: false, width: '15%' },
        { text: 'Rate periods', value: 'dates', sortable: false, width: '15%' },
        { text: 'Length of stay', value: 'los', sortable: false, width: '20%' },
        { text: 'Price per night ', value: 'ppn', sortable: false, width: '10%' },
        { text: 'Rooms/Places', value: 'rooms', sortable: false, width: '20%' },
        { align: 'end', value: 'actions', sortable: false, width: '5%' },
      ],
      days: ['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'],
    }
  },
  props: {
    place: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState(useRateStore, {
      rateFindByIds: 'findByIds',
    }),
    ...mapState(useUnitStore, {
      unitFindByRateId: 'findByRateId',
    }),
    rates () {
      return this.rateFindByIds(this.place.rates)
    },
    formatItems () {
      return sortObjects(
        this.rates.map((rate) => {
          return {
            units: this.unitFindByRateId(rate.id),
            rate,
            dates: rate.rateDates.map(date => `${moment(date.dates.start).format('DD/MM/YY')} - ${moment(date.dates.end).format('DD/MM/YY')}`),
            los: rate.rateLengthOfStays.length > 0 ? 'Applied' : 'Not applied',
            ppn: this.formatRates(rate),
          }
        }),
        'rate.description'
      )
    },
  },
  methods: {
    clone,
    sortObjects,
    formatRates,
    ...mapActions(useRateStore, ['httpDeleteRate']),
    deleteSubmitHandler (rate) {
      const placeClone = this.clone(this.place)
      return this.httpDeleteRate({ rate, place: placeClone })
    },
  },
  components: {
    Confirmation,
    PrimaryToolTip,
  },
}
</script>
